import {
  normalizeTireCombos,
  rimSpecsFromTireCombos,
  VEHICLE_STORAGE_KEY,
} from '@theme/fitment-matcher';
import { fetchMakes } from '@theme/vehicle-api';

/** @type {{ VEHICLE_API_BASE_URL: string, RIMDATA_API_PREFIX: string }} */
const CONFIG = {
  VEHICLE_API_BASE_URL: 'https://admin.jiaojiao.tech:8443',
  RIMDATA_API_PREFIX: '/app-api/store/rimdata',
};

const state = {
  selectedVehicle: null,
  formValues: {
    year: '',
    make: '',
    model: '',
    trim: '',
  },
  formLabels: {
    make: '',
    model: '',
    trim: '',
  },
  trimOptions: [],
  vehicleDetails: null,
};

function readVehicle() {
  try {
    const raw = window.localStorage.getItem(VEHICLE_STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v !== 'object') return null;
    if (v.subModel != null && (v.trim === undefined || v.trim === '')) {
      v.trim = v.subModel;
    }
    delete v.subModel;
    return v;
  } catch (error) {
    return null;
  }
}

function writeVehicle(vehicle) {
  if (!vehicle) {
    window.localStorage.removeItem(VEHICLE_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(VEHICLE_STORAGE_KEY, JSON.stringify(vehicle));
}

function vehicleLabel(vehicle) {
  if (!vehicle) return 'No vehicle selected';
  return [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(' ');
}

function pickFormValues(vehicle) {
  return {
    year: vehicle?.year ? String(vehicle.year) : '',
    make: vehicle?.makeSlug
      ? String(vehicle.makeSlug)
      : vehicle?.make
        ? String(vehicle.make)
        : '',
    model: vehicle?.modelSlug
      ? String(vehicle.modelSlug)
      : vehicle?.model
        ? String(vehicle.model)
        : '',
    trim: vehicle?.trimId ? String(vehicle.trimId) : vehicle?.trim ? String(vehicle.trim) : '',
  };
}

function pickFormLabels(vehicle) {
  return {
    make: vehicle?.make ? String(vehicle.make) : '',
    model: vehicle?.model ? String(vehicle.model) : '',
    trim: vehicle?.trim ? String(vehicle.trim) : '',
  };
}

function optionsFromValues(values, placeholder) {
  const html = [`<option value="">${placeholder}</option>`];
  values.forEach((value) => {
    const safe = String(value).replace(/"/g, '&quot;');
    html.push(`<option value="${safe}">${safe}</option>`);
  });
  return html.join('');
}

/** @param {{ value: string, label: string }[]} options */
function optionsFromItems(options, placeholder) {
  const html = [`<option value="">${placeholder}</option>`];
  options.forEach(({ value, label }) => {
    const safeValue = String(value).replace(/"/g, '&quot;');
    const safeLabel = String(label).replace(/</g, '&lt;').replace(/>/g, '&gt;');
    html.push(`<option value="${safeValue}">${safeLabel}</option>`);
  });
  return html.join('');
}

function getField(root, field) {
  return root.querySelector(`[data-vehicle-input="${field}"]`);
}

function setSelectLoading(select, loading, placeholder) {
  if (!(select instanceof HTMLSelectElement)) return;
  select.disabled = loading;
  if (loading) {
    select.innerHTML = `<option value="">${placeholder}</option>`;
  }
}

function populateSelect(select, values, placeholder, selectedValue = '') {
  if (!(select instanceof HTMLSelectElement)) return;
  const normalized = values.map((item) => String(item));
  select.innerHTML = optionsFromValues(normalized, placeholder);
  select.disabled = false;
  if (selectedValue && normalized.includes(String(selectedValue))) {
    select.value = String(selectedValue);
  } else {
    select.value = '';
  }
}

/** @param {{ value: string, label: string }[]} options */
function populateSelectItems(select, options, placeholder, selectedValue = '') {
  if (!(select instanceof HTMLSelectElement)) return;
  select.innerHTML = optionsFromItems(options, placeholder);
  select.disabled = false;
  const hasValue = options.some((option) => option.value === String(selectedValue));
  select.value = hasValue ? String(selectedValue) : '';
}

function resetSelect(select, placeholder) {
  if (!(select instanceof HTMLSelectElement)) return;
  select.innerHTML = optionsFromValues([], placeholder);
  select.disabled = true;
  select.value = '';
}

function syncFormLabelFromSelect(select, field) {
  if (!(select instanceof HTMLSelectElement)) return;
  const selectedOption = select.options[select.selectedIndex];
  state.formLabels[field] = selectedOption && selectedOption.value ? selectedOption.text : '';
}

async function vehicleApiFetch(path, params = {}) {
  const url = new URL(`${CONFIG.RIMDATA_API_PREFIX}${path}`, CONFIG.VEHICLE_API_BASE_URL);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });

  const response = await fetch(url.toString(), {
    headers: {
      Accept: '*/*',
      'tenant-id': '1',
    },
  });
  if (!response.ok) {
    throw new Error(`Vehicle API request failed: ${response.status}`);
  }

  const payload = await response.json();
  if (payload?.code !== 0 && payload?.code !== 200) {
    throw new Error(payload?.msg || 'Vehicle API request failed');
  }

  return payload.data;
}

function parseYearOption(item) {
  if (item == null) return '';
  if (typeof item === 'string' || typeof item === 'number') return String(item);
  return String(item.value ?? item.year ?? item.label ?? '');
}

function parseSlugLabelOption(item) {
  if (!item || typeof item !== 'object') {
    return { value: '', label: '' };
  }
  const value = String(item.value ?? item.slug ?? '').trim();
  const label = String(item.label ?? item.name ?? value).trim();
  return { value, label };
}

async function fetchYears() {
  const data = await vehicleApiFetch('/years');
  if (!Array.isArray(data)) return [];
  return data
    .map(parseYearOption)
    .filter(Boolean)
    .sort((a, b) => Number(b) - Number(a));
}

async function fetchModels(year, makeSlug) {
  if (!year || !makeSlug) return [];
  const data = await vehicleApiFetch('/models', { year, make: makeSlug });
  if (!Array.isArray(data)) return [];
  return data
    .map(parseSlugLabelOption)
    .filter((item) => item.value);
}

async function fetchDetails(year, makeSlug, modelSlug) {
  if (!year || !makeSlug || !modelSlug) return null;
  return vehicleApiFetch('/details', { year, make: makeSlug, model: modelSlug });
}

function flattenTrimsFromDetails(details) {
  const generations = Array.isArray(details?.generations) ? details.generations : [];
  const options = [];

  generations.forEach((generation) => {
    const generationName = generation?.name ? String(generation.name) : '';
    (generation?.trims || []).forEach((trim) => {
      if (!trim?.id) return;
      const trimName = trim?.trimName != null ? String(trim.trimName) : String(trim.id);
      options.push({
        value: String(trim.id),
        label: generationName ? `${trimName} (${generationName})` : trimName,
        trim,
      });
    });
  });

  return options;
}

function collectTrimsFromDetails(details) {
  const generations = Array.isArray(details?.generations) ? details.generations : [];
  return generations.flatMap((generation) => (generation?.trims || []).filter((trim) => trim?.id));
}

function emptyVehicleSpecs() {
  return {
    bolt_pattern: null,
    center_bore: null,
    et_range: null,
    rim_specs: [],
    tire_combos: [],
    drw: null,
    driveType: null,
    isDually: null,
  };
}

function readIsDually(trim) {
  if (trim?.drw === true) return true;
  if (trim?.drw === false) return false;
  if (trim?.isDually === true || trim?.is_dually === true) return true;
  if (trim?.isDually === false || trim?.is_dually === false) return false;
  return null;
}

function readDriveType(trim, isDually) {
  const raw = trim?.driveType ?? trim?.drive_type;
  if (raw != null && raw !== '') {
    const normalized = String(raw).trim().toUpperCase();
    if (normalized === 'DRW' || normalized === 'DUALLY') return 'DRW';
    if (normalized === 'SRW') return 'SRW';
  }
  if (isDually === true) return 'DRW';
  if (isDually === false) return 'SRW';
  return null;
}

function extractSpecsFromTrim(trim) {
  const tireCombos = normalizeTireCombos(trim?.tireCombos);
  const etValues = tireCombos
    .flatMap((combo) => [combo.offsetRangeMin, combo.offsetRangeMax, combo.rimEtOffset])
    .filter((value) => value != null && value !== '');
  const isDually = readIsDually(trim);

  return {
    bolt_pattern: trim?.boltPattern ?? null,
    center_bore: trim?.centerBore ?? null,
    et_range: etValues.length ? [Math.min(...etValues), Math.max(...etValues)] : null,
    rim_specs: rimSpecsFromTireCombos(tireCombos),
    tire_combos: tireCombos,
    drw: isDually,
    driveType: readDriveType(trim, isDually),
    isDually,
  };
}

function mergeRimSpecs(specsList) {
  /** @type {{ width: number | null, diameter: number }[]} */
  const merged = [];
  const seen = new Set();

  specsList.flatMap((specs) => specs.rim_specs || []).forEach((spec) => {
    const key =
      spec.width != null ? `${spec.width}|${spec.diameter}` : `|${spec.diameter}`;
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(spec);
  });

  return merged;
}

function extractSpecsFromTrims(trims) {
  if (!trims.length) return emptyVehicleSpecs();

  const specsList = trims.map((trim) => extractSpecsFromTrim(trim));
  const boltPatterns = [...new Set(specsList.map((specs) => specs.bolt_pattern).filter(Boolean))];
  const centerBores = [...new Set(specsList.map((specs) => specs.center_bore).filter((value) => value != null))];
  const etMins = specsList.map((specs) => specs.et_range?.[0]).filter((value) => value != null);
  const etMaxs = specsList.map((specs) => specs.et_range?.[1]).filter((value) => value != null);
  const rimSpecs = mergeRimSpecs(specsList);
  const tireCombos = normalizeTireCombos(
    specsList.flatMap((specs) => specs.tire_combos || [])
  );

  const driveTypes = [
    ...new Set(
      specsList
        .map((specs) => specs.driveType)
        .filter((value) => value != null && value !== '')
        .map((value) => String(value).toUpperCase())
    ),
  ];
  const duallyFlags = [...new Set(specsList.map((specs) => specs.isDually).filter((value) => value != null))];

  return {
    bolt_pattern: boltPatterns.length === 1 ? boltPatterns[0] : boltPatterns.length ? boltPatterns : null,
    center_bore: centerBores.length === 1 ? centerBores[0] : centerBores.length ? centerBores : null,
    et_range: etMins.length ? [Math.min(...etMins), Math.max(...etMaxs)] : null,
    rim_specs: rimSpecs,
    tire_combos: tireCombos,
    drw: duallyFlags.length === 1 ? duallyFlags[0] : null,
    driveType: driveTypes.length === 1 ? driveTypes[0] : null,
    isDually: duallyFlags.length === 1 ? duallyFlags[0] : null,
  };
}

function extractSpecsFromDetails(details) {
  if (!details) return emptyVehicleSpecs();

  if (details.model?.boltPattern || details.model?.tireCombos?.length) {
    return extractSpecsFromTrim(details.model);
  }

  const generations = Array.isArray(details.generations) ? details.generations : [];
  for (const generation of generations) {
    if (generation?.boltPattern || generation?.tireCombos?.length) {
      return extractSpecsFromTrim(generation);
    }
  }

  return extractSpecsFromTrims(collectTrimsFromDetails(details));
}

function updateTrimFieldVisibility(root) {
  const trimField = getField(root, 'trim');
  const fieldLabel = root.querySelector('[data-vehicle-trim-field]');
  const hasOptions = state.trimOptions.length > 0;

  if (fieldLabel instanceof HTMLElement) {
    fieldLabel.classList.toggle('hidden', !hasOptions);
  }

  if (trimField instanceof HTMLSelectElement && !hasOptions) {
    trimField.value = '';
  }
}

async function updateDependentOptions(root) {
  const { year, make, model, trim } = state.formValues;
  const makeField = getField(root, 'make');
  const modelField = getField(root, 'model');
  const trimField = getField(root, 'trim');

  if (!year) {
    resetSelect(makeField, 'Select vehicle make');
    resetSelect(modelField, 'Select vehicle model');
    resetSelect(trimField, 'Select vehicle trim (optional)');
    state.formValues.make = '';
    state.formValues.model = '';
    state.formValues.trim = '';
    state.formLabels = { make: '', model: '', trim: '' };
    state.trimOptions = [];
    state.vehicleDetails = null;
    updateTrimFieldVisibility(root);
    return;
  }

  setSelectLoading(makeField, true, 'Loading makes...');
  resetSelect(modelField, 'Select vehicle model');
  resetSelect(trimField, 'Select vehicle trim (optional)');
  state.trimOptions = [];
  state.vehicleDetails = null;

  try {
    const makes = await fetchMakes(year);
    if (!makes.some((item) => item.value === make)) {
      state.formValues.make = '';
      state.formLabels.make = '';
    }
    populateSelectItems(makeField, makes, 'Select vehicle make', state.formValues.make);
    syncFormLabelFromSelect(makeField, 'make');
  } catch (error) {
    console.warn('Failed to load makes.', error);
    resetSelect(makeField, 'Select vehicle make');
    state.formValues.make = '';
    state.formValues.model = '';
    state.formValues.trim = '';
    state.formLabels = { make: '', model: '', trim: '' };
    return;
  }

  if (!state.formValues.make) {
    state.formValues.model = '';
    state.formValues.trim = '';
    state.formLabels.model = '';
    state.formLabels.trim = '';
    return;
  }

  setSelectLoading(modelField, true, 'Loading models...');

  try {
    const models = await fetchModels(year, state.formValues.make);
    if (!models.some((item) => item.value === model)) {
      state.formValues.model = '';
      state.formLabels.model = '';
    }
    populateSelectItems(modelField, models, 'Select vehicle model', state.formValues.model);
    syncFormLabelFromSelect(modelField, 'model');
  } catch (error) {
    console.warn('Failed to load models.', error);
    resetSelect(modelField, 'Select vehicle model');
    state.formValues.model = '';
    state.formValues.trim = '';
    state.formLabels.model = '';
    state.formLabels.trim = '';
    return;
  }

  if (!state.formValues.model) {
    state.formValues.trim = '';
    state.formLabels.trim = '';
    state.vehicleDetails = null;
    updateTrimFieldVisibility(root);
    return;
  }

  setSelectLoading(trimField, true, 'Loading vehicle details...');

  try {
    const details = await fetchDetails(year, state.formValues.make, state.formValues.model);
    state.vehicleDetails = details;
    state.trimOptions = flattenTrimsFromDetails(details);
    if (!state.trimOptions.some((item) => item.value === trim)) {
      state.formValues.trim = '';
      state.formLabels.trim = '';
    }
    populateSelectItems(trimField, state.trimOptions, 'Select vehicle trim (optional)', state.formValues.trim);
    syncFormLabelFromSelect(trimField, 'trim');
    updateTrimFieldVisibility(root);
  } catch (error) {
    console.warn('Failed to load vehicle details.', error);
    resetSelect(trimField, 'Select vehicle trim (optional)');
    state.formValues.trim = '';
    state.formLabels.trim = '';
    state.trimOptions = [];
    state.vehicleDetails = null;
    updateTrimFieldVisibility(root);
  }
}

function broadcastVehicleChange() {
  window.dispatchEvent(new CustomEvent('vehiclefitment:change', { detail: { vehicle: state.selectedVehicle } }));
}

function updateSelectedUI(root) {
  const saved = root.querySelector('[data-vehicle-selector-saved]');
  const display = root.querySelector('[data-vehicle-selected-display]');
  const form = root.querySelector('[data-vehicle-selector-form]');
  const hasVehicle = Boolean(state.selectedVehicle);

  if (hasVehicle) {
    display.textContent = vehicleLabel(state.selectedVehicle);
  }

  saved.classList.toggle('hidden', !hasVehicle);
  form.classList.toggle('hidden', hasVehicle);
}

function updateVehicleBannerTriggers() {
  document.querySelectorAll('[data-vehicle-banner-trigger]').forEach((el) => {
    const defaultLabel = el.dataset.defaultLabel || 'Select My Vehicle';
    const label = state.selectedVehicle ? vehicleLabel(state.selectedVehicle) : defaultLabel;
    el.textContent = label;
    el.setAttribute('aria-label', label);
  });
}

function updateGlobalVehicleLabels() {
  updateVehicleBannerTriggers();
}

async function initVehicleSelector() {
  const root = document.querySelector('[data-vehicle-selector-root]');
  if (!root) return;

  const form = root.querySelector('[data-vehicle-selector-form]');
  const yearField = getField(root, 'year');
  const submitButton = form.querySelector('.vehicle-selector__submit');

  setSelectLoading(yearField, true, 'Loading years...');
  resetSelect(getField(root, 'make'), 'Select vehicle make');
  resetSelect(getField(root, 'model'), 'Select vehicle model');
  resetSelect(getField(root, 'trim'), 'Select vehicle trim (optional)');

  try {
    const years = await fetchYears();
    populateSelect(yearField, years, 'Select vehicle year');
  } catch (error) {
    console.warn('Failed to load years.', error);
    populateSelect(yearField, [], 'Select vehicle year');
  }

  state.selectedVehicle = readVehicle();
  if (state.selectedVehicle) {
    state.formValues = pickFormValues(state.selectedVehicle);
    state.formLabels = pickFormLabels(state.selectedVehicle);
    yearField.value = state.formValues.year;
    await updateDependentOptions(root);
    updateTrimFieldVisibility(root);
  }

  updateSelectedUI(root);
  updateGlobalVehicleLabels();
  if (state.selectedVehicle) {
    broadcastVehicleChange();
  }

  ['year', 'make', 'model', 'trim'].forEach((field) => {
    const input = getField(root, field);
    input.addEventListener('change', async (event) => {
      state.formValues[field] = /** @type {HTMLSelectElement} */ (event.target).value;
      if (field !== 'year') {
        syncFormLabelFromSelect(/** @type {HTMLSelectElement} */ (event.target), field);
      }

      if (field === 'year') {
        state.formValues.make = '';
        state.formValues.model = '';
        state.formValues.trim = '';
        state.formLabels = { make: '', model: '', trim: '' };
      } else if (field === 'make') {
        state.formValues.model = '';
        state.formValues.trim = '';
        state.formLabels.model = '';
        state.formLabels.trim = '';
      } else if (field === 'model') {
        state.formValues.trim = '';
        state.formLabels.trim = '';
      }

      await updateDependentOptions(root);
    });
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
    }

    try {
      const { year, make, model, trim } = state.formValues;
      if (!year || !make || !model) {
        throw new Error('Year, make, and model are required.');
      }

      let details = state.vehicleDetails;
      if (!details) {
        details = await fetchDetails(year, make, model);
        state.vehicleDetails = details;
      }

      const trimOption = trim ? state.trimOptions.find((item) => item.value === trim) : null;
      const specs = trimOption?.trim ? extractSpecsFromTrim(trimOption.trim) : extractSpecsFromDetails(details);

      state.selectedVehicle = {
        year,
        make: state.formLabels.make,
        makeSlug: make,
        model: state.formLabels.model,
        modelSlug: model,
        modelId: details?.model?.id ?? null,
        trim: trimOption ? state.formLabels.trim : '',
        trimId: trimOption ? trim : '',
        ...specs,
      };
      writeVehicle(state.selectedVehicle);
      updateSelectedUI(root);
      updateGlobalVehicleLabels();
      broadcastVehicleChange();
    } catch (error) {
      console.warn('Failed to save vehicle.', error);
    } finally {
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
      }
    }
  });

  root.querySelector('[data-vehicle-selector-clear]').addEventListener('click', async () => {
    state.selectedVehicle = null;
    writeVehicle(null);
    state.formValues = { year: '', make: '', model: '', trim: '' };
    state.formLabels = { make: '', model: '', trim: '' };
    state.trimOptions = [];
    state.vehicleDetails = null;
    yearField.value = '';
    await updateDependentOptions(root);
    updateSelectedUI(root);
    updateGlobalVehicleLabels();
    broadcastVehicleChange();
  });

  root.querySelector('[data-vehicle-selector-edit]').addEventListener('click', async () => {
    if (state.selectedVehicle) {
      state.formValues = pickFormValues(state.selectedVehicle);
      state.formLabels = pickFormLabels(state.selectedVehicle);
      yearField.value = state.formValues.year;
      await updateDependentOptions(root);
    }
    state.selectedVehicle = null;
    updateSelectedUI(root);
  });
}

function initNewsletterModal() {
  const modal = document.querySelector('[data-newsletter-modal]');
  if (!modal) return;
  const dismissedKey = 'methodNewsletterDismissed';
  if (window.localStorage.getItem(dismissedKey) === '1') return;

  window.setTimeout(() => {
    modal.hidden = false;
    document.body.classList.add('newsletter-modal-open');
  }, 1500);

  modal.querySelectorAll('[data-newsletter-close]').forEach((button) => {
    button.addEventListener('click', () => {
      modal.hidden = true;
      document.body.classList.remove('newsletter-modal-open');
      window.localStorage.setItem(dismissedKey, '1');
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initVehicleSelector();
  initNewsletterModal();
});
